import React, { useState, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import * as Themes from 'client/styles/theme';
import useLocalStorage from 'client/utils/use-local-storage';

type ThemeName = Exclude<keyof typeof Themes, 'default'>;

type ThemeType = import('client/styles/theme').ThemeType;

const defaultTheme = Themes.Main;
const defaultThemeName = 'Main';

type MutableThemeContextType = {
  theme: ThemeType;
  themeName: ThemeName;
  setTheme: (theme: ThemeType) => void;
  setThemeName: (name: ThemeName) => void;
};

const MutableThemeContext = React.createContext<MutableThemeContextType>({
  theme: defaultTheme,
  themeName: defaultThemeName,
  setTheme: () => {},
  setThemeName: () => {},
});

const MutableThemeProvider = ({ children }: { children: React.ReactElement }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [themeName, setThemeName] = useLocalStorage<ThemeName>('introwise-theme-name', defaultThemeName);

  useEffect(() => {
    if (themeName && Themes[themeName]) {
      setTheme(Themes[themeName]);
    } else {
      setTheme(defaultTheme);
    }
  }, [themeName]);

  return (
    <MutableThemeContext.Provider value={{ theme, themeName, setTheme, setThemeName }}>
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </MutableThemeContext.Provider>
  );
};

const MutableThemeConsumer = MutableThemeContext.Consumer;

export { MutableThemeConsumer, MutableThemeContext, MutableThemeProvider };
